import createFetchClient, {
  type Middleware,
  wrapAsPathBasedClient,
} from 'openapi-fetch';
import type {
  HttpMethod,
  PathsWithMethod,
  SuccessResponseJSON,
} from 'openapi-typescript-helpers';
import type { components, paths } from '~/api-types';
import { LOCAL_STORAGE_KEYS } from '~/Utils/Storage';
import env from '../Assets/env.json';

const getApiBaseUrl = () => {
  const baseUrl = env.endpoint;

  if (!baseUrl) {
    throw new Error('API base URL is not defined in environment variables');
  }

  return baseUrl;
};

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS['userToken']);

    if (accessToken) {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return request;
  },
};

const fetchClient = createFetchClient<paths>({
  baseUrl: getApiBaseUrl(),
  // Session cookie set by the API on login; the bearer header stays until
  // the cookie-only release
  credentials: 'include',
});

fetchClient.use(authMiddleware);

const $api = wrapAsPathBasedClient(fetchClient);

export class ApiError extends Error {
  status: number;
  /** Numeric error code from the response body, when the API provides one */
  code?: number;
  body: unknown;

  constructor(response: Response, body: unknown) {
    const details =
      body && typeof body === 'object'
        ? (body as { message?: string; error?: string; code?: number })
        : {};

    super(
      details.message ||
        details.error ||
        `Request failed with status ${response.status}`
    );

    this.name = 'ApiError';
    this.status = response.status;
    this.code = details.code;
    this.body = body;
  }
}

/**
 * Unwraps an openapi-fetch result: returns `data` on success,
 * throws an ApiError on HTTP errors.
 */
export const unwrap = async <T>(
  result: Promise<{ data?: T; error?: unknown; response: Response }>
): Promise<T> => {
  const { data, error, response } = await result;

  if (!response.ok) {
    throw new ApiError(response, error);
  }

  return data as T;
};

export { $api };
export type Api = components['schemas'];
export type InferResponseType<
  Path extends PathsWithMethod<paths, Method>,
  Method extends HttpMethod = 'get',
> = SuccessResponseJSON<NonNullable<paths[Path][Method]>>;
